import React, { useState } from 'react';
import { GenericTable } from '@shared/components/GenericTable';
import { Card, CardContent } from '@shared/ui/card';
import { Badge } from '@shared/ui/badge';
import { Dialog, DialogContent, DialogTitle } from '@shared/ui/dialog';
import { Eye, Receipt, CreditCard, User, Wallet, CheckCircle, FileText } from 'lucide-react';
import { QuickStatsCard } from '@shared/components/QuickStatsCard';

import { PaymentDetailsModal } from './PaymentHistory';

import { useCreditNoteDetail, useCreditNoteList } from '../../hook';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useCurrency } from '@shared/selectors/globalSelectors';
import { useTranslation } from 'react-i18next';

const CreditNoteDetailsModal = ({ note }: { note: any }) => {
  const { t } = useTranslation();
  
  const { data: creditNoteDetail } = useCreditNoteDetail({
    credit_note_id: note?.id,
    remote_utility_id: getRemoteUtilityId(),
  });

  const creditNoteInfo=creditNoteDetail?.result;

  if (!note) return null;
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start space-x-4 mb-2">
        <div className="bg-green-50 p-2.5 rounded-xl border border-green-100">
          <Receipt className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {t("Detailed information about credit note")} {creditNoteInfo?.receiptNo}
          </div>
        </div>
      </div>
      
      <div className="border border-gray-100 rounded-lg overflow-hidden shadow-sm">
        <div className="bg-gray-50/50 px-4 py-3 flex items-center space-x-2 border-b border-gray-100">
          <CreditCard className="w-4 h-4 text-gray-500" />
          <span className="font-semibold text-gray-800 text-sm">{t("Financial Details")}</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-y-6 gap-x-4 bg-white">
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Original Credit Amount")}</div>
            <div className="text-2xl font-bold text-green-600">{note.currency} {creditNoteInfo?.amount ?? 'NA'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Remaining Balance")}</div>
            <div className="text-2xl font-bold text-green-600">{creditNoteInfo?.financialDetails?.remainingBalance ?? 'NA'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Applied Amount")}</div>
            <div className="text-2xl font-bold text-blue-600">{creditNoteInfo?.financialDetails?.appliedAmount ?? 'NA'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Usage Percentage")}</div>
            <div className="text-2xl font-bold text-gray-900">{creditNoteInfo?.financialDetails?.usagePercentage || 'NA'}</div>
          </div>
        </div>
      </div>

      <div className="border border-gray-100 rounded-lg overflow-hidden shadow-sm">
        <div className="bg-gray-50/50 px-4 py-3 border-b border-gray-100">
          <span className="font-semibold text-gray-800 text-sm">{t("Credit Note Information")}</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-y-6 gap-x-4 bg-white">
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Reason")}</div>
            <div className="text-sm font-semibold text-gray-900">{note.creditNoteReason || 'NA'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Created By")}</div>
            <div className="flex items-center space-x-2 text-sm font-semibold text-gray-900">
              <User className="w-3.5 h-3.5 text-gray-400" />
              <span>{note.createdUserRemoteName || t("System")}</span>
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Linked Payment")}</div>
            <div className="text-sm font-semibold text-blue-600 cursor-pointer">{creditNoteInfo?.extraData?.originalReceiptNo || 'NA'}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground mb-1.5 font-medium tracking-wider">{t("Status")}</div>
            <Badge variant="secondary" className="bg-blue-50 text-blue-600 hover:bg-blue-50 border-transparent font-medium">
              {note.paymentReceivedStatus || 'NA'}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CreditNotes = () => {
  const { t } = useTranslation();
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [selectedLinkedPayment, setSelectedLinkedPayment] = useState<any>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const { consumerId } = getLoginDataFromStorage();

  const { data: notesData } = useCreditNoteList({
    remote_utility_id: getRemoteUtilityId(),
    consumer_id: consumerId,
    payment_pay_type: 3,
    page,
    limit
  });
  
  const currency = useCurrency();
  const notesRecords = notesData?.results || [];
  
  const totalCount = notesData?.count || 0;
  const totalPages = Math.ceil(totalCount / limit);
  const startItem = totalCount > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, totalCount);

  const columns = React.useMemo(() => [
    { header: t("Credit Note"), key: 'receiptNo' as const, render: (val: string, row: any) => <span className="font-medium text-blue-600">{val || row.id}</span> },
    { header: t("Amount"), key: 'amount' as const, render: (val: string) => <span>{currency} {val ?? 'NA'}</span> },
    { header: t("Reason"), key: 'creditNoteReason' as const, render: (val: any) => val || 'NA' },
    { 
      header: t("Status"), 
      key: 'paymentReceivedStatus' as const,
      type: 'badge',
      badgeConfig: {
        getVariant: (value: string) => {
          if (value === 'Posted') return 'default';
          if (value === 'Applied') return 'secondary';
          return 'outline';
        }
      }
    },
    { 
      header: t("Created On"), 
      key: 'paymentDate' as const, 
      render: (val: string, row: any) => (
        <div className="flex flex-col">
          <span className="font-medium text-slate-800">{val || 'NA'}</span>
          <span className="text-[11px] text-slate-500">{t("By")} {row.createdUserRemoteName || t("System")}</span>
        </div>
      )
    },
    { 
      header: t("Linked Payment"), 
      key: 'referenceReceiptNo' as const, 
      render: (val: string) => (
        <span 
          className="text-blue-600 cursor-pointer hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            if (val) {
              setSelectedLinkedPayment({
                receiptNo: val,
                amount: 'NA',
                paymentDate: 'NA',
                paymentMode: 'NA',
                paymentReceivedStatus: 'NA'
              });
            }
          }}
        >
          {val || 'NA'}
        </span>
      ) 
    },
  ], [currency, t]);

  const totalAmount = notesRecords.reduce((sum: number, n: any) => sum + Number(n.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <QuickStatsCard 
          title={t("Total Credit Notes")}
          count={totalCount}
          icon={<FileText className="h-4 w-4 text-gray-500" />}
        />
        <QuickStatsCard 
          title={t("Active Credits")}
          count={totalCount}
          icon={<CheckCircle className="h-4 w-4 text-green-500" />}
        />
        <QuickStatsCard 
          title={t("Available Balance")}
          displayValue={<span className="text-green-600 font-bold">{currency} {totalAmount.toFixed(2)}</span>}
          icon={<Wallet className="h-4 w-4 text-blue-500" />}
        />
      </div>

      <div className="rounded-md border border-gray-200 bg-white overflow-hidden shadow-sm">
        <GenericTable 
          columns={columns} 
          data={notesRecords}
          pagination={{
            currentPage: page,
            totalPages: totalPages || 1,
            startItem: startItem,
            endItem: endItem,
            totalItems: totalCount
          }}
          onPageChange={setPage}
          actions={[
            {
              label: t("View Details"),
              icon: <Eye className="w-4 h-4 text-gray-500" />,
              onClick: (row: any) => setSelectedNote({ ...row, currency }),
            }
          ]}
          actionsType="icons"
        />
      </div>

      <Dialog open={!!selectedNote} onOpenChange={(open) => !open && setSelectedNote(null)}>
        <DialogContent className="sm:max-w-xl p-0 border-none bg-white shadow-xl rounded-xl">
          <CreditNoteDetailsModal note={selectedNote} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedLinkedPayment} onOpenChange={(open) => !open && setSelectedLinkedPayment(null)}>
        <DialogContent className="sm:max-w-xl p-0 border-none bg-white shadow-xl rounded-xl">
          <PaymentDetailsModal payment={selectedLinkedPayment} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditNotes;
